package com.arena_web.ui.components.chat

import androidx.compose.runtime.*
import com.arena_web.data.dto.ChatDto
import com.arena_web.data.dto.ChatMemberDto
import com.arena_web.data.dto.MessageDto
import com.arena_web.data.dto.MessagesPageDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.widgets.Avatar
import com.arena_web.ui.components.widgets.AvatarSize
import com.arena_web.ui.components.widgets.Divider
import com.arena_web.ui.components.widgets.MediaLightbox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodySMedium
import com.arena_web.ui.theme.bodySRegular
import com.arena_web.ui.theme.bodyXlRegular
import com.arena_web.ui.theme.h2Medium
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Video

@Composable
fun ChatInfoPanel(
    chat: ChatDto,
    onlineUserIds: Set<String>,
    currentUserId: String?,
    onClose: () -> Unit,
    onMuteChat: () -> Unit,
    onLeaveChat: () -> Unit,
    onDeleteChat: () -> Unit,
    onFetchMessages: suspend (String, Map<String, String>) -> Result<MessagesPageDto> = { _, _ -> Result.failure(Exception("Not provided")) },
    onEditGroup: () -> Unit = {},
    onInviteMembers: () -> Unit = {},
    onRemoveMember: (String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    val isGroup = chat.type.uppercase() == "GROUP"
    // Admin = chat owner or an ADMIN-role member; gates the member remove (×).
    val myRole = chat.members.firstOrNull { it.userId == currentUserId }?.role?.uppercase()
    val isAdmin = chat.creatorId == currentUserId || myRole == "OWNER" || myRole == "ADMIN"

    Column(
        modifier = modifier
            .fillMaxHeight()
            .styleModifier {
                property("width", s(360))
                property("min-width", s(360))
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property("border-radius", "0 ${s(28)} ${s(28)} ${s(28)}")
                property("background",
                    "linear-gradient(215deg, rgba(60, 121, 241, 0.135) 37.5%, rgba(5, 4, 12, 0.03) 61.8%)"
                )
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("flex-shrink", "0")
                property("scrollbar-width", "none")
                property("padding-top", s(16))
                property("gap", s(18))
            }
    ) {
        // Header: X | "User Info"
        InfoHeader(
            title = if (isGroup) t.chatGroupInfoTitle else t.chatUserInfoTitle,
            isGroup = isGroup,
            onClose = onClose
        )

        // Avatar + name + status + action buttons
        AvatarSection(
            chat = chat,
            isGroup = isGroup,
            onlineUserIds = onlineUserIds,
            currentUserId = currentUserId,
            onEditGroup = onEditGroup,
        )

        Divider()

        // Notifications row with toggle
        NotificationRow(onToggle = onMuteChat)

        // Media tabs + grid (+ Members tab for groups)
        MediaSection(
            chatId = chat.id,
            isGroup = isGroup,
            members = chat.members,
            onlineUserIds = onlineUserIds,
            currentUserId = currentUserId,
            onFetchMessages = onFetchMessages,
            onInviteMembers = onInviteMembers,
            isAdmin = isAdmin,
            onRemoveMember = onRemoveMember,
        )

        // Destructive action
        InfoDivider()

        val isOwner = chat.creatorId == currentUserId

        DestructiveAction(
            label = when {
                !isGroup -> t.chatDeleteChatAction
                isOwner -> t.chatDeleteGroupAction
                else -> t.chatLeaveGroupAction
            },
            icon = if (!isGroup || isOwner) AppIcons.Trash else AppIcons.Enter,
            onClick = if (!isGroup || isOwner) onDeleteChat else onLeaveChat
        )

        Box(modifier = Modifier.sHeight(24))
    }
}

// ── Header ──

@Composable
private fun InfoHeader(
    title: String,
    isGroup: Boolean,
    onClose: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingLeft(16)
            .styleModifier {
                property("flex-shrink", "0")
                property("gap", s(12))
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Close button (X)
        Box(
            modifier = Modifier
                .sSize(24)
                .cursor(Cursor.Pointer)
                .onClick { onClose() },
            contentAlignment = Alignment.Center
        ) {
            Image(
                src = AppIcons.Close,
                modifier = Modifier.sSize(24)
            )
        }

        // Title
        SpanText(
            title,
            modifier = Modifier
                .bodyLMedium()
                .color(AppColors.Text.Secondary)
                .weight(1f)
        )
    }
}

// ── Avatar section ──

@Composable
private fun AvatarSection(
    chat: ChatDto,
    isGroup: Boolean,
    onlineUserIds: Set<String>,
    currentUserId: String?,
    onEditGroup: () -> Unit = {},
) {
    val t = LocalAppStrings.current
    // Direct-chat partner = first non-system member that ISN'T the viewer.
    // The previous selector ("first non-system member") was order-dependent
    // and frequently picked the current user, surfacing the viewer's own
    // avatar in their own User Info panel.
    val otherMember = if (!isGroup) {
        chat.members.firstOrNull { it.role != "SYSTEM" && it.userId != currentUserId }
            ?: chat.members.firstOrNull { it.role != "SYSTEM" }
    } else null
    val isOnline = otherMember?.let { onlineUserIds.contains(it.userId) } ?: false

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("gap", s(22))
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Avatar + Name + Status
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier { property("gap", s(12)) },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Avatar + Name group
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier { property("gap", s(18)) },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                val directCardFrameUrl = if (!isGroup) otherMember?.cardImageUrl else null
                val name = chat.name ?: otherMember?.username ?: t.chatDefaultName
                // Group admins get an edit pencil over the avatar. Backend gates
                // the actual update on OWNER/ADMIN; this only decides visibility.
                val myRole = chat.members.firstOrNull { it.userId == currentUserId }?.role?.uppercase()
                val isAdmin = chat.creatorId == currentUserId || myRole == "OWNER" || myRole == "ADMIN"
                Box(modifier = Modifier.styleModifier { property("position", "relative") }) {
                if (directCardFrameUrl != null) {
                    // Direct chat partner with a generated card — Figma
                    // 8538:398193 specs the header card at 93×140, which
                    // maps to PlayerCardSize.Arena (94×147). Medium (130×202)
                    // was distorting the aspect ratio inside that footprint
                    // and reading as squished.
                    Box(
                        modifier = Modifier.styleModifier {
                            property("flex-shrink", "0")
                            property("width", s(PlayerCardSize.Arena.w))
                            property("height", s(PlayerCardSize.Arena.h))
                        },
                        contentAlignment = Alignment.Center
                    ) {
                        PlayerCard(
                            playerName = "",
                            rating = 0,
                            position = "",
                            sport = "football",
                            size = PlayerCardSize.Arena,
                            cardFrameUrl = directCardFrameUrl,
                            avatarUrl = otherMember?.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                            hideRating = true,
                            hideShadow = true,
                        )
                    }
                } else {
                    // Group chat or direct chat without a generated card —
                    // keep the round 140×140 avatar tile with image / initials.
                    Box(
                        modifier = Modifier
                            .sSize(140)
                            .borderRadius(50.percent)
                            .styleModifier {
                                property("overflow", "hidden")
                                property("flex-shrink", "0")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        val imageUrl = if (!isGroup) otherMember?.avatarUrl else chat.imageUrl
                        if (imageUrl != null) {
                            val fallbackInitials = name.split(" ")
                                .take(2)
                                .mapNotNull { it.firstOrNull()?.uppercase() }
                                .joinToString("")
                            Div(attrs = {
                                ref { el ->
                                    el.innerHTML = "<img src=\"$imageUrl\" referrerpolicy=\"no-referrer\" " +
                                        "onerror=\"this.style.display='none';this.nextSibling.style.display='flex'\" " +
                                        "style=\"width:100%;height:100%;object-fit:cover;border-radius:50%;\" />" +
                                        "<span style=\"display:none;width:100%;height:100%;align-items:center;justify-content:center;" +
                                        "font-size:${s(40)};font-weight:600;color:rgba(59,130,246,0.8);\">$fallbackInitials</span>"
                                    onDispose {}
                                }
                                style { property("width", "100%"); property("height", "100%") }
                            })
                        } else {
                            val initials = name.split(" ")
                                .take(2)
                                .mapNotNull { it.firstOrNull()?.uppercase() }
                                .joinToString("")
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .borderRadius(50.percent)
                                    .backgroundColor(AppColors.Primary),
                                contentAlignment = Alignment.Center
                            ) {
                                SpanText(
                                    initials,
                                    modifier = Modifier
                                        .sFontSize(40)
                                        .color(AppColors.PrimaryWhite)
                                )
                            }
                        }
                    }
                }
                    if (isGroup && isAdmin) {
                        EditGroupPencil(onClick = onEditGroup)
                    }
                }

                // Name
                SpanText(
                    chat.name ?: otherMember?.fullName ?: otherMember?.username ?: t.chatDefaultName,
                    modifier = Modifier
                        .h2Medium()
                        .color(AppColors.Text.Secondary)
                )
            }

            // Status
            if (isGroup) {
                val onlineCount = chat.members.count { onlineUserIds.contains(it.userId) }
                SpanText(
                    if (onlineCount > 0) t.chatMembersCountOnline(chat.members.size, onlineCount)
                    else t.chatMembersCount(chat.members.size),
                    modifier = Modifier
                        .bodyLRegular()
                        .color(AppColors.Text.Muted)
                )
            } else {
                SpanText(
                    if (isOnline) t.chatOnline else t.chatLastSeenRecently,
                    modifier = Modifier
                        .bodyLRegular()
                        .color(if (isOnline) AppColors.PrimaryMint else AppColors.Text.Muted)
                )
            }
        }

    }
}

// ── Notification row with toggle ──

@Composable
private fun NotificationRow(onToggle: () -> Unit) {
    val t = LocalAppStrings.current
    var enabled by remember { mutableStateOf(true) }
    val sportColors = LocalSportColors.current

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(0, 16)
            .styleModifier { property("gap", s(12)) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Bell icon
        Image(
            src = AppIcons.Notifications,
            modifier = Modifier
                .sSize(24)
                .styleModifier {
                    property("flex-shrink", "0")
                }
        )

        // Label
        SpanText(
            t.chatNotificationsLabel,
            modifier = Modifier
                .bodyXlRegular()
                .color(AppColors.Text.Secondary)
                .weight(1f)
        )

        // Toggle switch
        Box(
            modifier = Modifier
                .sWidth(51)
                .sHeight(31)
                .sBorderRadius(16)
                .backgroundColor(
                    if (enabled) sportColors.primary else AppColors.Dark.SurfaceVariant
                )
                .cursor(Cursor.Pointer)
                .onClick {
                    enabled = !enabled
                    onToggle()
                }
                .styleModifier {
                    property("position", "relative")
                    property("transition", "background-color 0.2s ease")
                    property("flex-shrink", "0")
                }
        ) {
            // Thumb
            Box(
                modifier = Modifier
                    .sSize(27)
                    .borderRadius(50.percent)
                    .backgroundColor(AppColors.PrimaryWhite)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "2px")
                        property("transition", "left 0.2s ease")
                        if (enabled) {
                            property("left", s(22))
                        } else {
                            property("left", "2px")
                        }
                    }
            )
        }
    }
}

// ── Media section with tabs + grid ──

private val URL_REGEX = Regex("""https?://[^\s<>"{}|\\^`\[\]]+""")

private fun formatFileSize(bytes: Long?): String {
    if (bytes == null || bytes <= 0) return ""
    return when {
        bytes < 1024 -> "$bytes B"
        bytes < 1024 * 1024 -> "${bytes / 1024} KB"
        else -> {
            val mb = bytes.toDouble() / (1024 * 1024)
            val rounded = kotlin.math.round(mb * 10) / 10
            "$rounded MB"
        }
    }
}

private data class ExtractedLink(
    val url: String,
    val senderName: String?,
)

@Composable
private fun MediaSection(
    chatId: String,
    isGroup: Boolean,
    members: List<ChatMemberDto>,
    onlineUserIds: Set<String>,
    currentUserId: String?,
    onFetchMessages: suspend (String, Map<String, String>) -> Result<MessagesPageDto>,
    onInviteMembers: () -> Unit = {},
    isAdmin: Boolean = false,
    onRemoveMember: (String) -> Unit = {},
) {
    var activeTab by remember { mutableStateOf(if (isGroup) "Members" else "Media") }
    val tabs = if (isGroup) listOf("Members", "Media", "Files", "Links") else listOf("Media", "Files", "Links")

    // Per-tab caches
    var mediaMessages by remember { mutableStateOf<List<MessageDto>?>(null) }
    var fileMessages by remember { mutableStateOf<List<MessageDto>?>(null) }
    var linkMessages by remember { mutableStateOf<List<ExtractedLink>?>(null) }
    var loadingTab by remember { mutableStateOf<String?>(null) }

    // Reset caches when chatId changes
    LaunchedEffect(chatId) {
        mediaMessages = null
        fileMessages = null
        linkMessages = null
    }

    // Fetch data when tab changes or cache is empty
    LaunchedEffect(chatId, activeTab) {
        when (activeTab) {
            "Media" -> if (mediaMessages == null) {
                loadingTab = "Media"
                val result = onFetchMessages(chatId, mapOf("type" to "IMAGE,VIDEO,FILE", "size" to "50"))
                val all = result.getOrNull()?.messages ?: emptyList()
                mediaMessages = all.filter { msg ->
                    msg.fileUrl != null && run {
                        val mime = msg.mimeType?.lowercase() ?: ""
                        mime.startsWith("image/") || mime.startsWith("video/")
                    }
                }
                loadingTab = null
            }
            "Files" -> if (fileMessages == null) {
                loadingTab = "Files"
                val result = onFetchMessages(chatId, mapOf("type" to "FILE,AUDIO", "size" to "30"))
                val all = result.getOrNull()?.messages ?: emptyList()
                fileMessages = all.filter { msg ->
                    msg.fileUrl != null && run {
                        val mime = msg.mimeType?.lowercase() ?: ""
                        !mime.startsWith("image/") && !mime.startsWith("video/")
                    }
                }
                loadingTab = null
            }
            "Links" -> if (linkMessages == null) {
                loadingTab = "Links"
                val result = onFetchMessages(chatId, mapOf("type" to "TEXT", "size" to "50"))
                val texts = result.getOrNull()?.messages ?: emptyList()
                linkMessages = texts.flatMap { msg ->
                    URL_REGEX.findAll(msg.content).map { match ->
                        ExtractedLink(
                            url = match.value,
                            senderName = msg.senderUsername,
                        )
                    }.toList()
                }
                loadingTab = null
            }
        }
    }

    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current

    Column(modifier = Modifier.fillMaxWidth()) {
        // Tab bar with underline indicator
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                    property("flex-shrink", "0")
                }
        ) {
            // Tab labels
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(0, 40)
                    .styleModifier {
                        property("gap", s(40))
                        property("justify-content", "center")
                    }
            ) {
                tabs.forEach { tab ->
                    val isSelected = tab == activeTab
                    val tabLabel = when (tab) {
                        "Members" -> t.chatTabMembers
                        "Media" -> t.chatTabMedia
                        "Files" -> t.chatTabFiles
                        "Links" -> t.chatTabLinks
                        else -> tab
                    }
                    Column(
                        modifier = Modifier
                            .cursor(Cursor.Pointer)
                            .onClick { activeTab = tab }
                            .styleModifier {
                                property("gap", s(14))
                            },
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        SpanText(
                            tabLabel,
                            modifier = Modifier
                                .bodyXlRegular()
                                .color(if (isSelected) sportColors.primary else AppColors.Text.Secondary)
                                .whiteSpace(WhiteSpace.NoWrap)
                        )
                        // Tab underline indicator
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sHeight(2)
                                .sBorderRadius(1)
                                .backgroundColor(
                                    if (isSelected) sportColors.primary
                                    else com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0)
                                )
                                .styleModifier {
                                    property("transition", "background-color 0.2s ease")
                                }
                        )
                    }
                }
            }

            // Full-width gradient divider at the bottom
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(1)
                    .styleModifier {
                        property("background", AppGradients.BorderInnerL)
                        property("position", "absolute")
                        property("bottom", "0")
                        property("left", "0")
                    }
            )
        }

        Box(modifier = Modifier.sHeight(12))

        // Tab content
        when {
            loadingTab == activeTab -> {
                Box(
                    modifier = Modifier.fillMaxWidth().sPadding(32, 16),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.chatLoading,
                        modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
                    )
                }
            }
            activeTab == "Media" -> MediaGrid(mediaMessages ?: emptyList())
            activeTab == "Files" -> FilesList(fileMessages ?: emptyList())
            activeTab == "Links" -> LinksList(linkMessages ?: emptyList())
            activeTab == "Members" -> MembersContent(
                members = members,
                onlineUserIds = onlineUserIds,
                currentUserId = currentUserId,
                onInviteMembers = onInviteMembers,
                isAdmin = isAdmin,
                onRemoveMember = onRemoveMember
            )
        }
    }
}

// ── Media grid ──

@Composable
private fun MediaGrid(messages: List<MessageDto>) {
    val t = LocalAppStrings.current
    if (messages.isEmpty()) {
        EmptyTabState(t.chatNoMediaYet)
        return
    }

    var expandedUrl by remember { mutableStateOf<String?>(null) }
    var expandedIsVideo by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("gap", s(8))
                property("padding", s(8))
            }
    ) {
        messages.chunked(3).forEach { row ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("gap", s(8))
                        property("justify-content", "center")
                    }
            ) {
                row.forEach { msg ->
                    val isVideo = msg.mimeType?.lowercase()?.startsWith("video/") == true
                    Box(
                        modifier = Modifier
                            .sSize(110)
                            .sBorderRadius(10)
                            .backgroundColor(AppColors.Dark.SurfaceVariant)
                            .overflow(Overflow.Hidden)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                msg.fileUrl?.let {
                                    expandedUrl = it
                                    expandedIsVideo = isVideo
                                }
                            }
                            .styleModifier {
                                property("position", "relative")
                                property("flex-shrink", "0")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        val url = msg.fileUrl
                        if (url != null) {
                            if (isVideo) {
                                Video(
                                    attrs = {
                                        attr("src", url)
                                        attr("preload", "metadata")
                                        attr("muted", "")
                                        style {
                                            property("width", "100%")
                                            property("height", "100%")
                                            property("object-fit", "cover")
                                            property("pointer-events", "none")
                                        }
                                    }
                                )
                            } else {
                                Image(
                                    src = url,
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .fillMaxHeight()
                                        .objectFit(ObjectFit.Cover)
                                )
                            }
                        }
                        if (isVideo) {
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 100))
                                    .styleModifier {
                                        property("position", "absolute")
                                        property("top", "0")
                                        property("left", "0")
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                Image(
                                    src = AppIcons.Play,
                                    modifier = Modifier.sSize(16).styleModifier {
                                        property("filter", "brightness(2)")
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    if (expandedUrl != null) {
        MediaLightbox(
            url = expandedUrl!!,
            isVideo = expandedIsVideo,
            onDismiss = { expandedUrl = null }
        )
    }
}

// ── Files list ──

@Composable
private fun FilesList(messages: List<MessageDto>) {
    val t = LocalAppStrings.current
    if (messages.isEmpty()) {
        EmptyTabState(t.chatNoFilesYet)
        return
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(4, 8)
    ) {
        messages.forEach { msg ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(10, 12)
                    .sBorderRadius(8)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        msg.fileUrl?.let { window.open(it, "_blank") }
                    }
                    .styleModifier {
                        property("transition", "background-color 0.15s ease")
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .sSize(36)
                        .sBorderRadius(8)
                        .backgroundColor(AppColors.Dark.SurfaceVariant),
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        src = AppIcons.Document,
                        modifier = Modifier.sSize(18).styleModifier {
                            property("filter", "brightness(0.6)")
                        }
                    )
                }

                Column(
                    modifier = Modifier
                        .sPaddingLeft(12)
                        .weight(1f)
                        .overflow(Overflow.Hidden)
                ) {
                    SpanText(
                        msg.fileName ?: t.chatUnknownFile,
                        modifier = Modifier
                            .bodySMedium()
                            .color(AppColors.Text.Primary)
                            .whiteSpace(WhiteSpace.NoWrap)
                            .textOverflow(TextOverflow.Ellipsis)
                            .overflow(Overflow.Hidden)
                    )
                    val size = formatFileSize(msg.fileSize)
                    if (size.isNotEmpty()) {
                        SpanText(
                            size,
                            modifier = Modifier
                                .bodySRegular()
                                .color(AppColors.Text.Muted)
                                .sMarginTop(2)
                        )
                    }
                }
            }
        }
    }
}

// ── Links list ──

@Composable
private fun LinksList(links: List<ExtractedLink>) {
    val t = LocalAppStrings.current
    if (links.isEmpty()) {
        EmptyTabState(t.chatNoLinksYet)
        return
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(4, 8)
    ) {
        links.forEach { link ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(10, 12)
                    .sBorderRadius(8)
                    .cursor(Cursor.Pointer)
                    .onClick { window.open(link.url, "_blank") }
                    .styleModifier {
                        property("transition", "background-color 0.15s ease")
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .sSize(36)
                        .sBorderRadius(8)
                        .backgroundColor(AppColors.Dark.SurfaceVariant),
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        src = AppIcons.Globe,
                        modifier = Modifier.sSize(18).styleModifier {
                            property("filter", "brightness(0.6)")
                        }
                    )
                }

                Column(
                    modifier = Modifier
                        .sPaddingLeft(12)
                        .weight(1f)
                        .overflow(Overflow.Hidden)
                ) {
                    SpanText(
                        link.url,
                        modifier = Modifier
                            .bodySMedium()
                            .color(AppColors.Primary)
                            .whiteSpace(WhiteSpace.NoWrap)
                            .textOverflow(TextOverflow.Ellipsis)
                            .overflow(Overflow.Hidden)
                    )
                    val sender = link.senderName
                    if (sender != null) {
                        SpanText(
                            sender,
                            modifier = Modifier
                                .bodySRegular()
                                .color(AppColors.Text.Muted)
                                .sMarginTop(2)
                        )
                    }
                }
            }
        }
    }
}

// ── Empty tab state ──

@Composable
private fun EmptyTabState(text: String) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(32, 16),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            text,
            modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
        )
    }
}

// Edit-avatar pencil pinned to the group avatar's top-right (admins only).
@Composable
private fun EditGroupPencil(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(36)
            .borderRadius(50.percent)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "absolute")
                property("top", s(6))
                property("right", s(6))
                property("background", "#06122C")
                property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
            },
        contentAlignment = Alignment.Center
    ) {
        Image(
            src = AppIcons.Pencil,
            modifier = Modifier.sSize(16).styleModifier {
                property("filter", AppColors.IconDefaultFilter)
            }
        )
    }
}

// ── Members content (inside tabs) ──

@Composable
private fun MembersContent(
    members: List<ChatMemberDto>,
    onlineUserIds: Set<String>,
    currentUserId: String?,
    onInviteMembers: () -> Unit = {},
    isAdmin: Boolean = false,
    onRemoveMember: (String) -> Unit = {}
) {
    val t = LocalAppStrings.current
    if (members.isEmpty()) {
        EmptyTabState(t.chatNoMembers)
        return
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        // Invite members row
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .sPadding(10, 20)
                .cursor(Cursor.Pointer)
                .onClick { onInviteMembers() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .sSize(36)
                    .borderRadius(50.percent)
                    .backgroundColor(AppColors.Dark.SurfaceVariant),
                contentAlignment = Alignment.Center
            ) {
                Image(
                    src = AppIcons.Add,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("filter", "brightness(0.6)")
                    }
                )
            }
            SpanText(
                t.chatInviteMembersTitle,
                modifier = Modifier.bodyMRegular().color(AppColors.Primary).sPaddingLeft(12)
            )
        }

        members.forEach { member ->
            MemberItem(
                member = member,
                isOnline = onlineUserIds.contains(member.userId),
                // Admins can remove anyone but the owner and themselves.
                canRemove = isAdmin &&
                    member.userId != currentUserId &&
                    member.role.uppercase() != "OWNER",
                onRemove = { onRemoveMember(member.userId) }
            )
        }

        Box(modifier = Modifier.sHeight(8))
    }
}

// ── Member item ──

@Composable
private fun MemberItem(
    member: ChatMemberDto,
    isOnline: Boolean,
    canRemove: Boolean = false,
    onRemove: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(8, 20),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(modifier = Modifier.styleModifier { property("position", "relative") }) {
            // Mini player card when the member has one (same treatment as the
            // New Chat follow list), otherwise the round avatar fallback.
            val cardFrameUrl = member.cardImageUrl
            if (cardFrameUrl != null) {
                Box(
                    modifier = Modifier.styleModifier {
                        property("width", s(40))
                        property("height", s(60))
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("flex-shrink", "0")
                    },
                    contentAlignment = Alignment.Center
                ) {
                    PlayerCard(
                        playerName = "",
                        rating = 0,
                        position = "",
                        sport = "football",
                        size = PlayerCardSize.Highlight,
                        cardFrameUrl = cardFrameUrl,
                        avatarUrl = member.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                        hideRating = true,
                        hideShadow = true,
                    )
                }
            } else {
                Avatar(
                    imageUrl = member.avatarUrl,
                    name = member.username,
                    size = AvatarSize.SMALL
                )
            }
            if (isOnline) {
                Box(
                    modifier = Modifier
                        .sSize(10)
                        .borderRadius(50.percent)
                        .backgroundColor(AppColors.PrimaryMint)
                        .styleModifier {
                            property("position", "absolute")
                            property("bottom", "0")
                            property("right", "0")
                            property("border", "1.5px solid ${AppColors.Dark.Surface}")
                        }
                )
            }
        }

        Column(modifier = Modifier.sPaddingLeft(10).weight(1f)) {
            SpanText(
                member.fullName ?: member.username,
                modifier = Modifier.bodySMedium().color(AppColors.Text.Primary)
            )
            SpanText(
                if (isOnline) t.chatOnline else t.chatOffline,
                modifier = Modifier
                    .bodySRegular()
                    .color(if (isOnline) AppColors.PrimaryMint else AppColors.Text.Muted)
            )
        }

        val role = member.role.uppercase()
        if (role == "OWNER" || role == "ADMIN") {
            Box(
                modifier = Modifier
                    .sPadding(3, 8)
                    .sBorderRadius(4)
                    .backgroundColor(
                        if (role == "OWNER") com.varabyte.kobweb.compose.ui.graphics.Color.rgba(255, 165, 0, 40)
                        else com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 97, 255, 40)
                    ),
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    if (role == "OWNER") t.chatRoleOwner else t.chatRoleAdmin,
                    modifier = Modifier
                        .bodySRegular()
                        .color(
                            if (role == "OWNER") com.varabyte.kobweb.compose.ui.graphics.Color.rgb(255, 165, 0)
                            else AppColors.Primary
                        )
                )
            }
        }

        if (canRemove) {
            RemoveMemberButton(onRemove = onRemove)
        }
    }
}

// Bare red × by default; the red-tinted circle fades in on hover. The box stays
// a fixed size so the row never reflows.
@Composable
private fun RemoveMemberButton(onRemove: () -> Unit) {
    var active by remember { mutableStateOf(false) }
    Box(
        modifier = Modifier
            .sSize(36)
            .sMarginLeft(8)
            .borderRadius(50.percent)
            .cursor(Cursor.Pointer)
            .onClick { onRemove() }
            .onMouseEnter { active = true }
            .onMouseLeave { active = false }
            .styleModifier {
                property("flex-shrink", "0")
                property("transition", "background 0.15s ease, border 0.15s ease")
                if (active) {
                    property("background", "rgba(255, 66, 66, 0.15)")
                    property("border", "0.8px solid #FF4242")
                } else {
                    property("background", "transparent")
                    property("border", "0.8px solid transparent")
                }
            },
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier.sSize(14).styleModifier {
                property("background-color", "#FF4242")
                property("mask", "url('${AppIcons.Close}') no-repeat center / contain")
                property("-webkit-mask", "url('${AppIcons.Close}') no-repeat center / contain")
            }
        )
    }
}

// ── Destructive action ──

@Composable
private fun DestructiveAction(
    label: String,
    icon: String,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(14, 20)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(
            src = icon,
            modifier = Modifier.sSize(20).styleModifier {
                property("filter", "brightness(0) saturate(100%) invert(22%) sepia(95%) saturate(7480%) hue-rotate(358deg) brightness(101%) contrast(119%)")
            }
        )
        SpanText(
            label,
            modifier = Modifier.bodyMRegular().color(AppColors.Error).sPaddingLeft(12)
        )
    }
}

// ── Divider ──

@Composable
private fun InfoDivider() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(1)
            .styleModifier {
                property("background", AppGradients.BorderInnerL)
            }
    )
}
